import {
    CategoryScale, Chart as ChartJS, Legend, LinearScale, LineElement, PointElement, Title, Tooltip
} from 'chart.js';
import { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';

import { monthNames } from '../../../../util/constant';
import useLead from '../useLead';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface StatusBreakdown {
  status: string;
  count: number;
}

interface WeekData {
  week: string;
  totalCount: number;
  statusBreakdown: StatusBreakdown[];
}

interface MonthData {
  month: string;
  totalLeads: number;
  weeks: WeekData[];
}

interface StatusCount {
  status: string;
  count: number;
}

interface WeekWiseTotalLead {
  year: number;
  week: number;
  totalLeads: number;
}

interface GraphData {
  monthWiseData: MonthData[];
  overallStatusCounts: StatusCount[];
  dateRange: {
    startDate: string;
    endDate: string;
  };
  weekWiseTotalLeads?: WeekWiseTotalLead[];
}

const GraphLead = () => {
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [statusData, setStatusData] = useState<StatusCount[]>([]);
  const [weekData, setWeekData] = useState<WeekData[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [totalLeadsData, setTotalLeadsData] = useState<WeekWiseTotalLead[]>([]);

  const {
    handleGraph,
    graphData
  } = useLead();


  const getMonthNumber = (monthName: string) => {
    return monthNames.indexOf(monthName) + 1;
  };

  const getMonthName = (monthNumber: number) => {
    return monthNames[monthNumber - 1];
  };

  useEffect(() => {
    if (isInitialLoad) {
      const currentMonthName = getMonthName(new Date().getMonth() + 1);
      setSelectedMonth(currentMonthName);
      setIsInitialLoad(false);
    } else {
      handleGraph(selectedMonth);
    }
  }, [selectedMonth]);

  useEffect(() => {
    if (graphData?.monthWiseData) {
      // Find the data for selected month
      const monthNumber = getMonthNumber(selectedMonth);
      const monthData = graphData.monthWiseData.find((data) => {
        const [year, month] = data.month.split('-');
        return parseInt(month) === monthNumber;
      });

      if (monthData) {
        console.log("call")
        setWeekData(monthData.weeks);
        setStatusData(graphData.overallStatusCounts);
        setTotalLeadsData(graphData.weekWiseTotalLeads || []);
      } else {
        console.log("call2")
        setWeekData([]);
        setStatusData([]);
        setTotalLeadsData([]);
      }
    }
  }, [graphData, selectedMonth]);

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const monthName = e.target.value;
    setSelectedMonth(monthName);
  };

  const createChartData = (label: string, dataKey: string, color: string) => ({
    labels: weekData.map(item => item.week),
    datasets: [
      {
        label: label,
        data: weekData.map(item => {
          const statusData = item.statusBreakdown.find((s: any) => s.status === label);
          return statusData ? statusData.count : 0;
        }),
        borderColor: color,
        backgroundColor: color.replace('1)', '0.2)'),
        tension: 0.4,
      },
    ],
  });

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Lead Status Trends',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  const createMainChartData = () => {
    const colors = {
      'New': 'rgba(54, 162, 235, 1)',
      'Contacted': 'rgba(255, 206, 86, 1)',
      'Converted': 'rgba(75, 192, 192, 1)',
      'Lost': 'rgba(255, 99, 132, 1)',
      'On Hold': 'rgba(153, 102, 255, 1)',
      'Follow-Up': 'rgba(255, 159, 64, 1)',
      'Not Interested': 'rgba(201, 203, 207, 1)',
      'Approved': 'rgba(75, 192, 192, 1)',
      'Send For Review': 'rgba(255, 205, 86, 1)',
      'Reminder': 'rgba(255, 99, 132, 1)',
      'Duplicate': 'rgba(201, 203, 207, 1)'
    };

    // Get unique statuses from weekData
    const uniqueStatuses = Array.from(new Set(
      weekData.flatMap(week => week.statusBreakdown.map(s => s.status))
    ));

    const datasets = uniqueStatuses.map((status) => ({
      label: status,
      data: weekData.map((week) => {
        const statusData = week.statusBreakdown.find((s) => s.status === status);
        return statusData ? statusData.count : 0;
      }),
      borderColor: colors[status as keyof typeof colors] || 'rgba(0, 0, 0, 1)',
      backgroundColor: (colors[status as keyof typeof colors] || 'rgba(0, 0, 0, 1)').replace('1)', '0.2)'),
      tension: 0.4,
    }));

    return {
      labels: weekData.map(item => item.week),
      datasets
    };
  };

  const createTotalLeadsChartData = () => ({
    labels: totalLeadsData.map(item => `Week ${item.week}`),
    datasets: [
      {
        label: 'Total Leads',
        data: totalLeadsData.map(item => item.totalLeads),
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.4,
      },
    ],
  });

  return (
    <div className="p-6">
      <div className="mb-6">
        <select 
          value={selectedMonth} 
          onChange={handleMonthChange}
          className="p-2 border rounded-md"
        >
          {monthNames.map((month) => (
            <option key={month} value={month}>
              {month}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <Line 
            data={createMainChartData()} 
            options={{
              ...options,
              plugins: {
                ...options.plugins,
                title: {
                  ...options.plugins.title,
                  text: `Lead Status Trends - ${selectedMonth}`
                }
              }
            }}
          />
        </div>
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <Line 
            data={createTotalLeadsChartData()} 
            options={{
              ...options,
              plugins: {
                ...options.plugins,
                title: {
                  ...options.plugins.title,
                  text: `Total Leads per Week - ${selectedMonth}`
                }
              }
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
        {statusData.map((status) => (
          <div key={status.status} className="bg-white p-6 rounded-lg shadow-lg">
            <Line 
              data={createChartData(
                status.status,
                'count',
                status.status === 'New' ? 'rgba(54, 162, 235, 1)' :
                status.status === 'Contacted' ? 'rgba(255, 206, 86, 1)' :
                status.status === 'Converted' ? 'rgba(75, 192, 192, 1)' :
                status.status === 'Lost' ? 'rgba(255, 99, 132, 1)' :
                status.status === 'On Hold' ? 'rgba(153, 102, 255, 1)' :
                status.status === 'Follow-Up' ? 'rgba(255, 159, 64, 1)' :
                status.status === 'Not Interested' ? 'rgba(201, 203, 207, 1)' :
                status.status === 'Approved' ? 'rgba(75, 192, 192, 1)' :
                status.status === 'Send For Review' ? 'rgba(255, 205, 86, 1)' :
                status.status === 'Reminder' ? 'rgba(255, 99, 132, 1)' :
                'rgba(201, 203, 207, 1)'
              )}
              options={{
                ...options,
                plugins: {
                  ...options.plugins,
                  title: {
                    ...options.plugins.title,
                    text: `${status.status} Leads`
                  }
                }
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default GraphLead;
